// src/pages/quote/QuoteDetails.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  FileDown,
  CalendarDays,
  User2,
  Building2,
  DollarSign,
  Percent,
  Edit2,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Ban,
  Send,
} from "lucide-react";

import api from "../../api/axios";
import EditQuoteModal from "../../components/EditQuoteModal";
import SendQuoteModal from "../../components/SendQuoteModal";

import {
  acceptQuote as svcAcceptQuote,
  declineQuote as svcDeclineQuote,
  cancelQuote as svcCancelQuote,
  convertToInvoice as svcConvertToInvoice,
  convertToJob as svcConvertToJob,
  deleteQuote as svcDeleteQuote,
} from "../../services/quoteService";

/* ----------------------------- helpers ----------------------------- */
const cx = (...a) => a.filter(Boolean).join(" ");

const fmtMoney = (n = 0, c = "USD", locale = "en-US") =>
  new Intl.NumberFormat(locale, { style: "currency", currency: c }).format(
    typeof n === "number" ? n : Number(n || 0)
  );

/** Date formatter for timestamps (local time) — createdAt/updatedAt/activity */
const fmtDateLocal = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

/** Date+time formatter (local time) — email stats etc. */
const fmtDateTimeLocal = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Date-only formatter locked to UTC — avoids off-by-one for issue/expiry */
const fmtDateOnlyUTC = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : "—";

/** Convert any date to a UTC "midnight" date-only epoch for safe day math */
const epochUTCDateOnly = (d) => {
  if (!d) return NaN;
  const x = new Date(d);
  return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
};

/** Day difference in whole days using UTC date-only (prevents DST/offset drift) */
const daysBetweenUTC = (a, b) =>
  Math.floor(
    (epochUTCDateOnly(a) - epochUTCDateOnly(b)) / (1000 * 60 * 60 * 24)
  );

const role = () => (localStorage.getItem("role") || "admin").toLowerCase();
const canMutateRole = () => ["admin", "staff"].includes(role());

function LifecycleChip({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-800 border-slate-200",
    sent: "bg-indigo-100 text-indigo-800 border-indigo-200",
    accepted: "bg-green-100 text-green-700 border-green-200",
    declined: "bg-rose-100 text-rose-700 border-rose-200",
    expired: "bg-amber-100 text-amber-700 border-amber-200",
    canceled: "bg-zinc-100 text-zinc-800 border-zinc-200",
  };
  const cls = map[(status || "draft").toLowerCase()] || map.draft;
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
        cls
      )}
    >
      {(status || "draft").toUpperCase()}
    </span>
  );
}

/* ----------------------------- address helpers ----------------------------- */
const fmtAddressMultiline = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  if (addr.raw) return addr.raw;

  const line1 = addr.line1 || addr.street || addr.street1 || "";
  const line2 = addr.line2 || addr.street2 || "";
  const city = addr.city || "";
  const region = addr.state || addr.region || addr.province || "";
  const postal = addr.postalCode || addr.zip || "";
  const country = addr.country || "";

  const rows = [];
  if (line1) rows.push(String(line1).trim());
  if (line2) rows.push(String(line2).trim());
  const cityRow = [city, region, postal].filter(Boolean).join(", ");
  if (cityRow) rows.push(cityRow);
  if (country) rows.push(country);

  return rows.join("\n");
};

/* ----------------------------- main ----------------------------- */
export default function QuoteDetails() {
  const { id: routeId = "" } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showSend, setShowSend] = useState(false);

  /* ----------------------------- API fetchers ----------------------------- */
  const fetchQuote = useCallback(async (id) => {
    try {
      const res = await api.get(`/quotes/${id}?expand=links`);
      return res?.data?.data || null;
    } catch (err) {
      if (err?.response?.status === 400) toast.error("Invalid quote id");
      return null;
    }
  }, []);

  const fetchClient = useCallback(async (clientId) => {
    if (!clientId) return null;
    try {
      const res = await api.get(`/clients/${clientId}`);
      return res?.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  /* ----------------------------- data load ----------------------------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = await fetchQuote(routeId);
      if (!q) {
        setQuote(null);
        setClient(null);
        return;
      }
      setQuote(q);
      const [clientData] = await Promise.all([fetchClient(q.clientId)]);
      setClient(clientData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error("Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [routeId, fetchQuote, fetchClient]);

  useEffect(() => {
    load();
  }, [load]);

  /* ----------------------------- computed ----------------------------- */

  // Company-level prefs (locale, currency) coming from snapshot
  const companySnapshot =
    quote?.company || quote?.companySnapshot || quote?.billFrom || null;

  const companyLocale = companySnapshot?.locale || "en-US";
  const companyCurrency = companySnapshot?.currency || "USD";

  const currency = (quote?.currency || companyCurrency || "USD").toUpperCase();

  const linkedWork = useMemo(() => {
    const w = quote?.workId;
    if (!w) return null;
    if (typeof w === "string")
      return { _id: w, workNumber: null, status: null, type: null };
    return {
      _id: w?._id || w?.id,
      workNumber: w?.workNumber,
      status: w?.status,
      type: w?.type,
    };
  }, [quote]);

  const linkedInvoice = useMemo(() => {
    const inv = quote?.invoiceId;
    if (!inv) return null;
    if (typeof inv === "string")
      return {
        _id: inv,
        invoiceNumber: null,
        status: null,
        totalAmount: null,
      };
    return {
      _id: inv?._id,
      invoiceNumber: inv?.invoiceNumber,
      status: inv?.status,
      totalAmount: inv?.totalAmount,
    };
  }, [quote]);

  const serverSubtotal = Number(quote?.subtotal ?? 0);
  const serverDiscount = Number(quote?.discountTotal ?? 0);
  const serverTax = Number(quote?.taxTotal ?? 0);
  const serverTotal = Number(quote?.totalAmount ?? quote?.total ?? 0);

  const clientSubtotal = useMemo(() => {
    if (!quote?.lineItems?.length) return serverSubtotal || serverTotal;
    return quote.lineItems.reduce((s, li) => {
      const qty = Number(li.quantity || 0);
      const price = Number(li.unitPrice || li.rate || 0);
      return s + qty * price;
    }, 0);
  }, [quote, serverSubtotal, serverTotal]);

  // Total discount = line-level discount + quote-level discountTotal
  const lineDiscountTotal = Math.max(0, clientSubtotal - serverSubtotal);
  const discountTotal = lineDiscountTotal + serverDiscount;

  const taxTotal = serverTax;
  const computedTotal =
    serverTotal || Math.max(0, clientSubtotal - discountTotal) + taxTotal;

  // Expiry badge with UTC-safe day math
  const expiryBadge = useMemo(() => {
    if (!quote?.expiryDate) return null;
    const delta = daysBetweenUTC(quote.expiryDate, new Date());
    if (delta > 0) return `Expires in ${Math.abs(delta)}d`;
    if (delta === 0) return "Expires today";
    return `Expired ${Math.abs(delta)}d ago`;
  }, [quote?.expiryDate]);

  const status = String(quote?.status || "draft").toLowerCase();

  // Frontend "expired" hard-lock aligned with backend
  const isExpired = useMemo(() => {
    if (!quote) return false;
    if (status === "expired") return true;
    if (quote.expiryDate) {
      const dt = new Date(quote.expiryDate);
      return Number.isFinite(+dt) && dt < new Date();
    }
    return false;
  }, [quote, status]);

  const isEditLocked =
    ["accepted", "canceled"].includes(status) || !!linkedInvoice || isExpired;
  const isActionLocked = ["accepted", "canceled", "expired"].includes(status);
  const canConvert = !["declined", "canceled", "expired"].includes(status);

  // Terms label using UTC-safe day math
  const terms = useMemo(() => {
    if (!quote?.issueDate || !quote?.expiryDate) return quote?.terms || "—";
    const d = daysBetweenUTC(quote.expiryDate, quote.issueDate);
    return d > 0 ? `Valid ${d} days` : quote?.terms || "—";
  }, [quote]);

  const taxGroups = useMemo(() => {
    const m = new Map();
    (quote?.lineItems || []).forEach((li) => {
      const pct = Number(li?.taxPercent || 0);
      const key = Number.isFinite(pct) ? pct : 0;
      const amt = Number(li?.taxAmount || 0);
      m.set(key, (m.get(key) || 0) + amt);
    });
    return m;
  }, [quote]);

  const nonZeroTaxPercents = [...taxGroups.keys()].filter((p) => p > 0);
  const taxHeaderLabel =
    nonZeroTaxPercents.length === 1
      ? `Tax (${nonZeroTaxPercents[0]}%)`
      : "Tax";
  const taxBreakdownRows = [...taxGroups.entries()]
    .filter(([pct, amt]) => pct > 0 && amt > 0)
    .sort((a, b) => a[0] - b[0]);

  const co = useMemo(() => {
    const bf = quote?.billFrom || {};
    const hasBF =
      !!(
        bf?.name ||
        bf?.email ||
        bf?.phone ||
        bf?.taxId ||
        bf?.logoUrl ||
        (bf?.address && Object.values(bf.address).some(Boolean))
      );
    return hasBF ? bf : quote?.companySnapshot || quote?.company || {};
  }, [quote]);

  const fmtCoAddress = (a) => fmtAddressMultiline(a) || "—";

  // -------- Bill To (company + contact + address) ----------
  const billToCompany =
    quote?.clientSnapshot?.name || client?.name || quote?.clientName || "—";
  const billToContactName =
    quote?.clientSnapshot?.contactName ||
    client?.primaryContact?.name ||
    client?.contactName ||
    client?.contact?.fullName ||
    client?.contact?.name ||
    "";

  const billToEmail =
    quote?.clientSnapshot?.email ||
    client?.primaryContact?.email ||
    client?.email ||
    client?.contact?.email ||
    "";
  const billToPhone =
    quote?.clientSnapshot?.phone ||
    client?.primaryContact?.phone ||
    client?.phone ||
    client?.contact?.phone ||
    "";

  const billToAddress =
    quote?.clientSnapshot?.address ||
    client?.billingAddress ||
    client?.address ||
    quote?.billTo?.address ||
    null;

  const billToAddressText = fmtAddressMultiline(billToAddress);

  /* ----------------------------- Email activity computed ----------------------------- */
  const emailStats = quote?.emailStats || {};
  const lastSentAt = emailStats?.lastSentAt || quote?.sentAt || null;

  // Public view counters on the quote page (distinct from email opens)
  const viewCount =
    (quote?.public && Number(quote.public.viewCount)) ||
    (quote?.viewStats && Number(quote.viewStats.count)) ||
    0;
  const lastViewAt =
    quote?.public?.lastViewAt ||
    quote?.viewStats?.lastAt ||
    quote?.firstViewedAt ||
    null;

  // Decision info
  const acceptedAt =
    quote?.acceptance?.acceptedAt || quote?.decision?.acceptedAt || null;
  const declinedAt = quote?.decision?.declinedAt || null;
  const declineReason = quote?.decision?.declineReason || "";

  // Optional delivery/engagement stats
  const deliveredCount = Number.isFinite(emailStats?.deliveredCount)
    ? Number(emailStats.deliveredCount)
    : null;
  const bouncedCount = Number.isFinite(emailStats?.bouncedCount)
    ? Number(emailStats.bouncedCount)
    : null;
  const droppedCount = Number.isFinite(emailStats?.droppedCount)
    ? Number(emailStats.droppedCount)
    : null;
  const complainedCount = Number.isFinite(emailStats?.complainedCount)
    ? Number(emailStats.complainedCount)
    : null;
  const failedCount = Number.isFinite(emailStats?.failedCount)
    ? Number(emailStats.failedCount)
    : null;

  const openCount = Number.isFinite(emailStats?.openCount)
    ? Number(emailStats.openCount)
    : null;
  const clickCount = Number.isFinite(emailStats?.clickCount)
    ? Number(emailStats.clickCount)
    : null;

  const lastDeliveredAt =
    emailStats?.lastDeliveredAt || emailStats?.deliveredAt || null;
  const lastBouncedAt =
    emailStats?.lastBouncedAt || emailStats?.bouncedAt || null;
  const lastDroppedAt =
    emailStats?.lastDroppedAt || emailStats?.droppedAt || null;
  const lastComplainedAt =
    emailStats?.lastComplainedAt || emailStats?.complainedAt || null;
  const lastFailedAt =
    emailStats?.lastFailedAt || emailStats?.failedAt || null;

  const lastOpenAt = emailStats?.lastOpenAt || emailStats?.openedAt || null;
  const lastClickAt =
    emailStats?.lastClickAt || emailStats?.clickedAt || null;

  const recipientsCount = Number.isFinite(emailStats?.recipientsCount)
    ? Number(emailStats.recipientsCount)
    : Array.isArray(emailStats?.recipients)
    ? emailStats.recipients.length
    : null;

  /* ----------------------------- actions ----------------------------- */
  const onAccept = async () => {
    if (!quote || !canMutateRole() || isActionLocked) return;
    if (!window.confirm(`Mark quote ${quote.quoteNumber || ""} as accepted?`))
      return;
    try {
      await svcAcceptQuote(quote._id, {});
      toast.success("Quote accepted");
      load();
    } catch {
      toast.error("Failed to accept quote");
    }
  };

  const onDecline = async () => {
    if (!quote || !canMutateRole() || isActionLocked) return;
    if (!window.confirm(`Mark quote ${quote.quoteNumber || ""} as declined?`))
      return;
    try {
      await svcDeclineQuote(quote._id, {});
      toast.success("Quote declined");
      load();
    } catch {
      toast.error("Failed to decline quote");
    }
  };

  const onCancel = async () => {
    if (!quote || !canMutateRole()) return;
    if (!window.confirm("Cancel this quote?")) return;
    try {
      await svcCancelQuote(quote._id, {});
      toast.success("Quote canceled");
      load();
    } catch {
      toast.error("Failed to cancel quote");
    }
  };

  const onConvertInvoice = async () => {
    if (!quote || !canMutateRole() || !canConvert) return;
    if (
      !window.confirm(
        `Convert quote ${quote.quoteNumber || ""} to an invoice?`
      )
    )
      return;
    try {
      const resp = await svcConvertToInvoice(quote._id, {});
      const newId =
        resp?.data?.invoiceId ||
        resp?.invoiceId ||
        resp?.data?.id ||
        resp?.id;
      toast.success("Converted to invoice");
      if (newId) navigate(`/invoices/${newId}`);
      else load();
    } catch {
      toast.error("Failed to convert to invoice");
    }
  };

  const onConvertJob = async () => {
    if (!quote || !canMutateRole() || !canConvert) return;
    if (
      !window.confirm(`Create a job from quote ${quote.quoteNumber || ""}?`)
    )
      return;
    try {
      const resp = await svcConvertToJob(quote._id, {});
      const newId =
        resp?.data?.workId || resp?.workId || resp?.data?.id || resp?.id;
      toast.success("Job created from quote");
      if (newId) navigate(`/work/${newId}`);
      else load();
    } catch {
      toast.error("Failed to convert to job");
    }
  };

  // Download-only PDF (no new tab, no preview)
  const onPdf = async () => {
    try {
      if (!quote?._id) throw new Error("Missing quote id");

      const fileName = `${quote.quoteNumber || "quote"}.pdf`.replace(
        /[^\w.-]+/g,
        "_"
      );

      const resp = await api.get(`/quotes/${quote._id}/pdf`, {
        responseType: "blob",
        params: {
          download: 1,
          v: quote?.updatedAt ? Number(new Date(quote.updatedAt)) : undefined,
        },
      });

      const blob = resp?.data;

      const ct = String(resp?.headers?.["content-type"] || "").toLowerCase();
      if (ct.includes("application/json")) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || "{}");
          throw new Error(json?.error || "Server error while generating PDF");
        } catch {
          throw new Error(text || "Server error while generating PDF");
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Empty PDF response");
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.message || "PDF download failed");
    }
  };

  // Archive action
  const onDelete = async () => {
    if (!quote || !canMutateRole()) return;
    const qn = quote.quoteNumber || "this quote";
    if (!window.confirm(`Archive ${qn}? This action cannot be undone.`)) return;
    try {
      await svcDeleteQuote(quote._id);
      toast.success("Quote archived");
      navigate("/quotes");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.response?.data?.error || "Failed to archive quote");
    }
  };

  const onRefreshCompanySnapshot = async () => {
    if (!quote || !canMutateRole()) return;
    if (isExpired) {
      toast.error("Quote is expired; duplicate as new to update company info.");
      return;
    }
    try {
      await api.put(`/quotes/${quote._id}`, {
        refreshCompanySnapshot: true,
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.error || "Failed to refresh company info"
      );
      return;
    }
    toast.success("Company info refreshed on quote");
    load();
  };

  /* ----------------------------- renderers ----------------------------- */
  const MoneyStat = ({ label, value, icon: Icon }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        {Icon ? <Icon size={16} /> : null}
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">
        {fmtMoney(value, currency, companyLocale)}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="animate-pulse rounded-2xl bg-slate-100 h-40 mb-6" />
        <div className="animate-pulse rounded-2xl bg-white h-[70vh] shadow" />
      </div>
    );
  }
  if (!quote) return <div className="p-6">Quote not found.</div>;

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden print:bg-white print:shadow-none print:border-0">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-slate-900 text-xl sm:text-2xl font-semibold mr-1">
                    {quote.quoteNumber ||
                      (quote._id ? `Q-${String(quote._id).slice(-6)}` : "")}
                  </h1>
                  <LifecycleChip status={status} />
                  {expiryBadge ? (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      {expiryBadge}
                    </span>
                  ) : null}

                  {/* Job / Invoice chips or actions */}
                  {linkedWork ? (
                    <Link
                      to={`/work/${linkedWork._id || linkedWork.id}`}
                      className="ml-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700 hover:bg-slate-100"
                    >
                      Job&nbsp;#{linkedWork.workNumber || "—"} ·{" "}
                      {linkedWork.status || "—"}
                    </Link>
                  ) : (
                    canMutateRole() &&
                    canConvert && (
                      <button
                        onClick={onConvertJob}
                        className="ml-1 inline-flex items-center rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
                        title="Create a job from this quote"
                      >
                        Convert to Job
                      </button>
                    )
                  )}

                  {linkedInvoice ? (
                    <Link
                      to={`/invoices/${linkedInvoice._id}`}
                      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700 hover:bg-slate-100"
                    >
                      Invoice&nbsp;#
                      {linkedInvoice.invoiceNumber || "—"} ·{" "}
                      {linkedInvoice.status || "—"}
                    </Link>
                  ) : (
                    canMutateRole() &&
                    canConvert && (
                      <button
                        onClick={onConvertInvoice}
                        className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
                        title="Convert this quote to an invoice"
                      >
                        Convert to Invoice
                      </button>
                    )
                  )}
                </div>
                <p className="text-slate-600 text-sm mt-1">
                  Quote details, pricing, and validity window
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (isExpired) {
                    toast.error("Quote is expired; duplicate as new to send.");
                    return;
                  }
                  setShowSend(true);
                }}
                disabled={isExpired}
                className={cx(
                  "rounded-xl border px-3 py-2",
                  isExpired
                    ? "border-slate-200 bg-slate-200 text-white cursor-not-allowed"
                    : "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                )}
                title={
                  isExpired
                    ? "Expired quotes cannot be sent"
                    : "Send quote via email"
                }
              >
                <Send size={16} className="inline -mt-0.5" />{" "}
                <span className="ml-1 hidden sm:inline">Send</span>
              </button>
              <button
                onClick={onPdf}
                className="rounded-xl border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
              >
                <FileDown size={16} className="inline -mt-0.5" />{" "}
                <span className="ml-1 hidden sm:inline">PDF</span>
              </button>

              {canMutateRole() && (
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={() =>
                      !isEditLocked ? navigate(`/quotes/${quote._id}/edit`) : null
                    }
                    disabled={isEditLocked}
                    title={
                      isEditLocked
                        ? "Locked after Accept/Cancel/Expired or conversion"
                        : "Full Edit"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 border",
                      isEditLocked
                        ? "border-slate-200 text-slate-400 cursor-not-allowed opacity-50"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <Edit2 size={16} className="inline -mt-0.5" />
                    <span className="ml-1">Full Edit</span>
                  </button>

                  <button
                    onClick={onAccept}
                    disabled={isActionLocked}
                    title={
                      isActionLocked
                        ? "Action not available on accepted/canceled/expired quotes"
                        : "Accept"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 text-white",
                      isActionLocked
                        ? "bg-slate-300 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    )}
                  >
                    <CheckCircle2 size={16} className="inline -mt-0.5" />{" "}
                    <span className="ml-1">Accept</span>
                  </button>
                  <button
                    onClick={onDecline}
                    disabled={isActionLocked}
                    title={
                      isActionLocked
                        ? "Action not available on accepted/canceled/expired quotes"
                        : "Decline"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 text-white",
                      isActionLocked
                        ? "bg-slate-300 cursor-not-allowed"
                        : "bg-rose-600 hover:bg-rose-700"
                    )}
                  >
                    <XCircle size={16} className="inline -mt-0.5" />{" "}
                    <span className="ml-1">Decline</span>
                  </button>

                  <button
                    onClick={onCancel}
                    disabled={status === "canceled"}
                    title={
                      status === "canceled"
                        ? "Already canceled"
                        : "Cancel quote"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 text-white",
                      status === "canceled"
                        ? "bg-slate-300 cursor-not-allowed"
                        : "bg-zinc-700 hover:bg-black/80"
                    )}
                  >
                    <Ban size={16} className="inline -mt-0.5" />{" "}
                    <span className="ml-1">Cancel</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Money quick stats */}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MoneyStat
              label="Subtotal"
              value={clientSubtotal}
              icon={DollarSign}
            />
            <MoneyStat label={taxHeaderLabel} value={taxTotal} icon={Percent} />
            <MoneyStat
              label="Discount"
              value={-discountTotal}
              icon={Percent}
            />
            <MoneyStat label="Total" value={computedTotal} icon={DollarSign} />
          </div>
        </div>

        {/* content */}
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Main */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company + Meta */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {co.logoUrl ? (
                      <img
                        src={co.logoUrl}
                        alt={co.name || "Company"}
                        className="h-10 w-10 rounded-xl object-contain bg-white border border-slate-200"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                        <Building2 size={18} />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">
                          {co.name || "Your Company"}
                        </span>
                        {canMutateRole() && (
                          <button
                            onClick={onRefreshCompanySnapshot}
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"
                            title="Refresh from Company"
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                      </div>
                      {co.legalName && (
                        <div className="text-sm text-slate-500">
                          {co.legalName}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-slate-600">
                        {[co.email, co.phone].filter(Boolean).join(" · ") ||
                          "—"}
                      </div>
                      {co.address && (
                        <div className="mt-1 text-xs text-slate-600 whitespace-pre-line">
                          {fmtCoAddress(co.address)}
                        </div>
                      )}
                      {co.taxId && (
                        <div className="mt-1 text-xs text-slate-600">
                          Tax ID: {co.taxId}
                        </div>
                      )}
                      {co.website && (
                        <a
                          href={co.website}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-xs text-indigo-600 hover:underline"
                        >
                          {co.website}
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                      <div className="text-slate-500">Status</div>
                      <div className="flex justify-end">
                        <LifecycleChip status={status} />
                      </div>

                      <div className="text-slate-500">Quote #</div>
                      <div className="text-right font-semibold">
                        {quote.quoteNumber || "—"}
                      </div>

                      <div className="text-slate-500">Issue</div>
                      <div className="text-right flex items-center justify-end gap-1">
                        <CalendarDays
                          size={14}
                          className="text-slate-400"
                        />{" "}
                        {fmtDateOnlyUTC(quote.issueDate)}
                      </div>

                      <div className="text-slate-500">Expiry</div>
                      <div className="text-right flex items-center justify-end gap-1">
                        <CalendarDays
                          size={14}
                          className="text-slate-400"
                        />{" "}
                        {fmtDateOnlyUTC(quote.expiryDate)}
                      </div>

                      <div className="text-slate-500">Total</div>
                      <div className="text-right font-semibold">
                        {fmtMoney(computedTotal, currency, companyLocale)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bill To */}
                <div className="mt-6">
                  <div className="text-sm font-semibold text-slate-800">
                    Bill To
                  </div>
                  <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Left: identity + contact */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-800">
                          <User2 size={16} className="text-slate-500" />
                          <span className="font-medium">
                            {billToCompany}
                          </span>
                          {client?._id ? (
                            <Link
                              className="ml-2 text-indigo-600 text-sm hover:underline"
                              to={`/clients/${client._id}`}
                            >
                              View client
                            </Link>
                          ) : null}
                        </div>

                        {billToContactName && (
                          <div className="mt-1 text-sm text-slate-700">
                            {billToContactName}
                          </div>
                        )}

                        {(billToEmail || billToPhone) && (
                          <div className="mt-1 text-sm text-slate-600 flex flex-col gap-0.5">
                            {billToEmail && (
                              <a
                                className="hover:underline"
                                href={`mailto:${billToEmail}`}
                              >
                                {billToEmail}
                              </a>
                            )}
                            {billToPhone && (
                              <a
                                className="hover:underline"
                                href={`tel:${billToPhone}`}
                              >
                                {billToPhone}
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: address aligned right on desktop */}
                      {billToAddressText && (
                        <div className="md:text-right text-sm text-slate-700 whitespace-pre-line">
                          {billToAddressText}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900 mb-4">
                  Items & Services
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-sm text-slate-600">
                        <th className="px-4 py-2 font-semibold">
                          Description
                        </th>
                        <th className="px-4 py-2 font-semibold w-24 text-right">
                          Qty
                        </th>
                        <th className="px-4 py-2 font-semibold w-32 text-right">
                          Rate
                        </th>
                        <th className="px-4 py-2 font-semibold w-32 text-right">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {(quote?.lineItems || []).map((li, idx) => {
                        const qty = Number(li.quantity || 0);
                        const price = Number(li.unitPrice || li.rate || 0);
                        const amount = qty * price;
                        return (
                          <tr key={idx} className="text-sm">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">
                                {li.description || li.name || "Item"}
                              </div>
                              {li?.notes ? (
                                <div className="text-slate-500">
                                  {li.notes}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {qty}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {fmtMoney(price, currency, companyLocale)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {fmtMoney(amount, currency, companyLocale)}
                            </td>
                          </tr>
                        );
                      })}
                      {!quote?.lineItems?.length && (
                        <tr>
                          <td
                            className="px-4 py-6 text-slate-500 text-sm"
                            colSpan={4}
                          >
                            No line items.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Side total card */}
                <div className="mt-6 flex justify-end">
                  <div className="w-full sm:w-96 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>Subtotal</span>
                      <span className="tabular-nums">
                        {fmtMoney(
                          clientSubtotal,
                          currency,
                          companyLocale
                        )}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                      <span>Discounts</span>
                      <span className="tabular-nums">
                        -
                        {fmtMoney(
                          discountTotal,
                          currency,
                          companyLocale
                        )}
                      </span>
                    </div>

                    {taxBreakdownRows.length > 1 && (
                      <div className="mt-2 space-y-1 text-sm text-slate-600">
                        {taxBreakdownRows.map(([pct, amt]) => (
                          <div
                            key={pct}
                            className="flex items-center justify-between"
                          >
                            <span>{`Tax ${pct}%`}</span>
                            <span className="tabular-nums">
                              {fmtMoney(
                                amt,
                                currency,
                                companyLocale
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                      <span>{taxHeaderLabel}</span>
                      <span className="tabular-nums">
                        {fmtMoney(taxTotal, currency, companyLocale)}
                      </span>
                    </div>

                    <div className="mt-3 border-t pt-3 flex items-center justify-between text-base font-semibold text-slate-900">
                      <span>Total</span>
                      <span className="tabular-nums">
                        {fmtMoney(
                          computedTotal,
                          currency,
                          companyLocale
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes & Activity */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-base font-semibold text-slate-900 mb-2">
                    Customer Notes
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {quote?.notes || "—"}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-base font-semibold text-slate-900 mb-2">
                    Activity
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li>Created: {fmtDateLocal(quote?.createdAt)}</li>
                    {quote?.sentAt && (
                      <li>Sent: {fmtDateLocal(quote.sentAt)}</li>
                    )}
                    {quote?.firstViewedAt && (
                      <li>
                        First Viewed:{" "}
                        {fmtDateLocal(quote.firstViewedAt)}
                      </li>
                    )}
                    {quote?.acceptance?.acceptedAt && (
                      <li>
                        Accepted:{" "}
                        {fmtDateLocal(quote.acceptance.acceptedAt)}
                      </li>
                    )}
                    {quote?.lastStatusChangeAt && (
                      <li>
                        Last Status Change:{" "}
                        {fmtDateLocal(quote.lastStatusChangeAt)}
                      </li>
                    )}
                    <li>
                      Current Status:{" "}
                      {String(quote?.status || "draft").toUpperCase()}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">
                  Quote Meta
                </div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Currency</dt>
                    <dd className="font-medium">{currency}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Terms</dt>
                    <dd className="font-medium">{terms}</dd>
                  </div>
                  {quote?.poNumber && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">PO #</dt>
                      <dd className="font-medium">{quote.poNumber}</dd>
                    </div>
                  )}
                  {quote?.assignedTo?.name && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Owner</dt>
                      <dd className="font-medium">
                        {quote.assignedTo.name}
                      </dd>
                    </div>
                  )}
                </dl>

                {canMutateRole() && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() =>
                        !isEditLocked ? setShowEdit(true) : null
                      }
                      disabled={isEditLocked}
                      title={
                        isEditLocked
                          ? "Locked after Accept/Cancel/Expired or conversion"
                          : "Quick Edit"
                      }
                      className={cx(
                        "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2",
                        isEditLocked
                          ? "border border-slate-200 text-slate-400 cursor-not-allowed opacity-50"
                          : "border border-slate-200 text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <Edit2 size={16} />{" "}
                      {isEditLocked ? "View (Locked)" : "Edit"}
                    </button>

                    <button
                      onClick={onDelete}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 size={16} /> Archive
                    </button>
                  </div>
                )}
              </div>

              {/* Email Activity */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">
                  Email Activity
                </div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Status</dt>
                    <dd className="font-medium">
                      {lastSentAt ? "Sent" : "Not sent yet"}
                    </dd>
                  </div>

                  {lastSentAt && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Last Sent</dt>
                      <dd className="font-medium">
                        {fmtDateTimeLocal(lastSentAt)}
                      </dd>
                    </div>
                  )}

                  {typeof recipientsCount === "number" &&
                    recipientsCount >= 0 && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Recipients</dt>
                        <dd className="font-medium">
                          {recipientsCount}
                        </dd>
                      </div>
                    )}

                  {deliveredCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Delivered</dt>
                      <dd className="font-medium">
                        {deliveredCount}
                        {lastDeliveredAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastDeliveredAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {openCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Opened</dt>
                      <dd className="font-medium">
                        {openCount}
                        {lastOpenAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastOpenAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {clickCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Clicked</dt>
                      <dd className="font-medium">
                        {clickCount}
                        {lastClickAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastClickAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {bouncedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Bounced</dt>
                      <dd className="font-medium">
                        {bouncedCount}
                        {lastBouncedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastBouncedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {droppedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Dropped</dt>
                      <dd className="font-medium">
                        {droppedCount}
                        {lastDroppedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastDroppedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {complainedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Complaints</dt>
                      <dd className="font-medium">
                        {complainedCount}
                        {lastComplainedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastComplainedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {failedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Failed</dt>
                      <dd className="font-medium">
                        {failedCount}
                        {lastFailedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastFailedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {viewCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Public Views</dt>
                      <dd className="font-medium">
                        {viewCount}
                        {lastViewAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastViewAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {acceptedAt && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Accepted</dt>
                      <dd className="font-medium">
                        {fmtDateTimeLocal(acceptedAt)}
                      </dd>
                    </div>
                  )}

                  {declinedAt && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Declined</dt>
                      <dd className="font-medium">
                        {fmtDateTimeLocal(declinedAt)}
                        {declineReason ? (
                          <span
                            className="block text-xs text-slate-500 mt-0.5"
                            title={declineReason}
                          >
                            {declineReason}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Linked Records */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">
                  Linked Records
                </div>

                {/* Job row */}
                <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Job</div>
                    {linkedWork ? (
                      <div className="text-slate-600">
                        #{linkedWork.workNumber || "—"} ·{" "}
                        {linkedWork.status || "—"}
                      </div>
                    ) : (
                      <div className="text-slate-500">No job linked</div>
                    )}
                  </div>
                  <div>
                    {linkedWork ? (
                      <Link
                        to={`/work/${linkedWork._id || linkedWork.id}`}
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </Link>
                    ) : (
                      canMutateRole() &&
                      canConvert && (
                        <button
                          onClick={onConvertJob}
                          className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          Convert to Job
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Invoice row */}
                <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Invoice</div>
                    {linkedInvoice ? (
                      <div className="text-slate-600">
                        #{linkedInvoice.invoiceNumber || "—"} ·{" "}
                        {linkedInvoice.status || "—"}{" "}
                        {Number.isFinite(linkedInvoice.totalAmount) && (
                          <span className="ml-1">
                            ·{" "}
                            {fmtMoney(
                              linkedInvoice.totalAmount,
                              currency,
                              companyLocale
                            )}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-500">
                        No invoice linked
                      </div>
                    )}
                  </div>
                  <div>
                    {linkedInvoice ? (
                      <Link
                        to={`/invoices/${linkedInvoice._id}`}
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </Link>
                    ) : (
                      canMutateRole() &&
                      canConvert && (
                        <button
                          onClick={onConvertInvoice}
                          className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          Convert to Invoice
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Removed: Addresses card / Attachments card */}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEdit && (
        <EditQuoteModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          quoteId={quote._id}
          onUpdated={load}
        />
      )}

      {showSend && (
        <SendQuoteModal
          isOpen={showSend}
          onClose={() => setShowSend(false)}
          quote={quote}
          lastSentAt={quote?.emailStats?.lastSentAt || quote?.sentAt}
          onSent={() => {
            toast.success("Queued to send");
            load();
          }}
        />
      )}
    </div>
  );
}
